// src/api.js
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "../utils/toast";
import translations from "../components/common/Translations";
import { withApiError, withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;

// UI should use `multi_image` for multiple images. Keep backward compatibility:
// - backend may send `multi_image`
// - older saved workflows may have `file` + `multiple: true`
function normalizeMultiImageFieldTypes(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item || typeof item !== "object") return item;
    const next = { ...item };

    const isLegacyMulti =
      next.fieldType === "file" && (next.multiple === true || next.multiple === "true");

    if (next.fieldType === "multi_image" || isLegacyMulti) {
      next.fieldType = "multi_image";
      next.multiple = true;
      next.accept = next.accept || "image/*";
    }

    if (Array.isArray(next.radioInputValues)) {
      next.radioInputValues = normalizeMultiImageFieldTypes(next.radioInputValues);
    }
    return next;
  });
}

export const fetchData = async (selectedOrgId, userType) => {
  console.log("selectedOrgId ==>", selectedOrgId)
  const token = Cookies.get("accessToken");
  let apiUrl = `${baseURL}api/v1/workflow/get-workflow-list`;

  // if (userType === "super_admin" && selectedOrgId) {
  apiUrl += `?projectId=${selectedOrgId}`;
  // }

  try {
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const jsonData = await response.json();
    return jsonData.body.value;
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

export const deleteWorkflow = async (workflowId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.delete(
      `${baseURL}api/v1/workflow/delete`,
      {
        params: { workflowId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (response.status === 200) {
      const jsonData = response.data;
      if (jsonData.header.code === 600) {
        return jsonData.body.value;
      } else {
        throw new Error(apiErrorMessage(jsonData));
      }
    } else {
      throw new Error(`Received non-200 status code: ${response.status}`);
    }
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

export const fetchFormData = async (workflowId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-metadata`,
      {
        params: { workflowId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const data = response.data;
    if (data.header.code === 600) {
      const updatedFieldsMetadata = data.body.value.fieldsMetadata.map(
        (field) => {
          const { workflowId, id, ...rest } = field;
          return rest;
        }
      );
      const normalizedFieldsMetadata =
        normalizeMultiImageFieldTypes(updatedFieldsMetadata);
      const originalWorkflowName = data.body.value.workflowName;
      const copiedWorkflowName = `${originalWorkflowName}_v1`;
      return {
        ...data.body.value,
        workflowName: copiedWorkflowName,
        fieldsMetadata: normalizedFieldsMetadata,
        workflowId: undefined,
      };
    } else {
      throw new Error("Failed to fetch resources: " + apiErrorMessage(data));
    }
  } catch (error) {
    throw new Error("Error fetching form data: " + error.message);
  }
};


export const fetchWorkflowData = async (workflowId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-metadata`,
      {
        params: { workflowId },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = response.data;
    if (data.header.code === 600) {
      return {
        workflowTimer: data.body.value.timer,
        workflowFields: normalizeMultiImageFieldTypes(
          data.body.value.fieldsMetadata
        ),
        existWorkflowName: data.body.value.workflowName,
        existPdfTitleName: data.body.value.pdfTitle,
        existDragItems: normalizeMultiImageFieldTypes(data.body.value.dragItems),
        existRadioButtons: data.body.value.radioButtons,
        accountProtectionCheck: data.body.value.accountProtectionCheck,
        clockin: data.body.value.clockIn,
        clockout: data.body.value.clockOut,
        isPageWise: data.body.value.isPageWise,
        // ------- sakshi -------- start
        isReviewEnabled: data.body.value.isReviewEnabled,
        // ------- sakshi -------- end
        isShowDistance: data.body.value.isShowDistance,
        // Safety compliance. `isSafetyComplianceRequired` is null on workflows
        // created before the feature shipped -- treat that as false.
        isSafetyComplianceRequired: Boolean(
          data.body.value.isSafetyComplianceRequired
        ),
        safetyWorkflowId: data.body.value.safetyWorkflowId ?? null,
        mainWorkflowId: data.body.value.mainWorkflowId ?? null,
      };
    } else {
      throw new Error("Failed to fetch data: " + apiErrorMessage(data));
    }
  } catch (error) {
    throw new Error("Error fetching workflow data: " + error.message);
  }
};
/* ---------------------------------------------------------------------------
 * Safety-compliance sub-workflow
 *
 * A safety workflow is a child row of a main workflow (`parentWorkflowId`). It is
 * created inline with the main workflow on create-workflow (nested `safetyWorkflow`),
 * but for an existing workflow it has its own CRUD below. Ordering rules enforced
 * by the backend:
 *   - enabling `isSafetyComplianceRequired` on /workflow/update requires the safety
 *     workflow to already exist, so always sync the child before the parent;
 *   - deleting the safety workflow requires the flag to be off first.
 * ------------------------------------------------------------------------- */

// Returns the new safety workflow id (Long) on success, otherwise throws with the
// backend's own validation message so callers can surface it verbatim.
export const createSafetyWorkflow = async (payload) => {
  const token = Cookies.get("accessToken");
  const response = await fetch(`${baseURL}api/v1/safety-workflow/create`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const jsonData = await response.json();
  if (jsonData?.header?.code !== 600) {
    throw new Error(apiErrorMessage(jsonData, "Failed to create safety workflow"));
  }
  return jsonData.body.value;
};

export const updateSafetyWorkflow = async (payload) => {
  const token = Cookies.get("accessToken");
  const response = await fetch(`${baseURL}api/v1/safety-workflow/update`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const jsonData = await response.json();
  if (jsonData?.header?.code !== 600) {
    throw new Error(apiErrorMessage(jsonData, "Failed to update safety workflow"));
  }
  return jsonData.body.value;
};

// Soft delete, keyed by the *main* workflow id. Disable safety compliance first.
export const deleteSafetyWorkflow = async (mainWorkflowId) => {
  const token = Cookies.get("accessToken");
  const response = await fetch(
    `${baseURL}api/v1/safety-workflow/delete?mainWorkflowId=${mainWorkflowId}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const jsonData = await response.json();
  if (jsonData?.header?.code !== 600) {
    throw new Error(apiErrorMessage(jsonData, "Failed to delete safety workflow"));
  }
  return jsonData.body.value;
};

// Same metadata shape as get-workflow-metadata. Returns null when the workflow has
// no safety workflow configured (code 607) -- that's a normal state, not an error.
export const fetchSafetyWorkflowData = async (mainWorkflowId) => {
  const token = Cookies.get("accessToken");
  const response = await axios.get(`${baseURL}api/v1/safety-workflow/get`, {
    params: { mainWorkflowId },
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const data = response.data;
  if (data?.header?.code === 607) return null;
  if (data?.header?.code !== 600) {
    throw new Error(apiErrorMessage(data, "Failed to fetch safety workflow"));
  }
  return {
    safetyWorkflowId: data.body.value.workflowId,
    safetyWorkflowName: data.body.value.workflowName,
    safetyPdfTitle: data.body.value.pdfTitle,
    safetyTimer: data.body.value.timer,
    safetyFields: normalizeMultiImageFieldTypes(data.body.value.fieldsMetadata),
    safetyDragItems: normalizeMultiImageFieldTypes(data.body.value.dragItems),
    safetyRadioButtons: data.body.value.radioButtons ?? [],
  };
};

// Start Harshit Vachhani 12-04-2025
export const getWorkflows = async (projectId) => {
  const token = Cookies.get("accessToken");

  const response = await axios.get(`${baseURL}api/v1/workflow/get-activities`, { // update end point by harshit 23-12-2025
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { projectId },
  });

  return withApiError(response.data);
};
// End Harshit Vachhani 12-04-2025

//Rc activity
export const getRcWorkflows = async (organizationId) => {
  const token = Cookies.get("accessToken");
  const response = await axios.get(
    `${baseURL}api/v1/workflow/get-workflows-rc?organizationId=${organizationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return withApiError(response.data);
};

export const cloneWorkflow = async (workflowName, maxAllowedAccuracy, selectedTimer, droppedItems, selectedOrgId, title, projectId, isPageWise, isReviewEnabled, isShowDistance, isSafetyComplianceRequired = false, safetyWorkflow = null) => {
  const token = Cookies.get("accessToken");
  const dataToSend = {
    timer: selectedTimer,
    workflowName: workflowName,
    pdfTitle: title,
    fieldsMetadata: droppedItems,
    organizationId: selectedOrgId,
    projectId: projectId,
    isPageWise,
    isReviewEnabled,
    isShowDistance,
    isSafetyComplianceRequired,
  };
  // create-workflow creates the parent and its safety child in one transaction.
  if (safetyWorkflow) {
    dataToSend.safetyWorkflow = safetyWorkflow;
  }
  const url = `${baseURL}api/v1/workflow/create-workflow`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataToSend),
    });

    const jsonData = await response.json();
    return withApiError(jsonData)
  } catch (err) {
    throw new Error("Error cloning workflow: " + err.message);
  }
};

export const createWorkflow = async (workflowData) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/workflow/create-workflow`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(workflowData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      toast.error(
        `Failed to create workflow: ${errorData.message || "Unknown error"}`
      );
      return;
    }

    const jsonData = await response.json();
    return withApiError(jsonData);
  } catch (err) {
    toast.error(`Error creating workflow: ${err.message}`);
    return null;
  }
};

export const fetchWorkflowHistory = async (workflowId, pages, size,langMode) => {
  const token = Cookies.get("accessToken");

  if (!workflowId) {
    toast.info(translations[langMode].createworkflowdiagram);
    return null;
  }

  const url = `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/workflow/get-workflow-history?workflowId=${workflowId}&page=${pages}&size=${size}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorMessage = await response.json();
      toast.error(`Failed to fetch versions: ${errorMessage.message || "Unknown error"}`);
      return null;
    }

    const jsonData = await response.json();
    return withApiError(jsonData);
  } catch (err) {
    toast.error(`${err.message}`);
    return null;
  }
};


export const updateWorkflow = async (workflowData) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/workflow/update`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(workflowData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      toast.error(
        `Failed to update workflow: ${errorData.message || "Unknown error"}`
      );
      return null;
    }

    const jsonData = await response.json();
    return withApiError(jsonData);
  } catch (err) {
    toast.error(`Error updating workflow: ${err.message}`);
    return null;
  }
};


export const getFieldNames = async (workflowId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/task-meta/workflow/get-field-names?workflowId=${workflowId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return withApiError(response.data); // Assuming you need the data from the response
  } catch (error) {
    console.error('Error fetching field names:', error);
    throw withApiErrorResponse(error); // Re-throwing to handle errors in the component
  }
};


export const getField = async (workflowId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/task-meta/task-details/columns?workflowId=${workflowId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return withApiError(response.data); // Assuming you need the data from the response
  } catch (error) {
    console.error('Error fetching field names:', error);
    throw withApiErrorResponse(error); // Re-throwing to handle errors in the component
  }
};


export const updatedMandatory = async (data) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/task-meta/task-details/mandatory`,
      data, // Pass the data directly as the second argument for a POST request
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data); // Return the data from the response
  } catch (error) {
    console.error('Error updating mandatory fields:', error);
    throw withApiErrorResponse(error);
  }
};
